/**
 * ============================================
 * ROLE PRECEDENCE
 * ============================================
 *
 * Single source of truth for resolving a user's effective role from their
 * Cognito groups list. A user may technically belong to multiple groups;
 * we collapse those down to ONE role using a fixed precedence:
 *
 *   admin > external > monitor
 *
 * - admin    = full control (failover mutations, settings, remote-config edit)
 * - external = silent viewer (all read views, NO alerts/alarms)
 * - monitor  = monitor-tab-only viewer WITH alerts/alarms
 *
 * A user in groups ['admin', 'monitor'] resolves to 'admin' (gets the
 * higher-power experience, not the restricted one). This avoids the
 * surprise of "I added myself to monitor to test and now I can't pause
 * the engine."
 *
 * Returns `null` when the user has NO recognised role (no Cognito group at
 * all, or only unknown groups). The dashboard treats null as "no access" --
 * we will not render the monitoring UI for an unauthorised account, even
 * though Cognito let them through the OAuth flow.
 */

export type Role = 'admin' | 'external' | 'monitor';

// Order matters: first match wins.
const ROLE_PRECEDENCE: Role[] = ['admin', 'external', 'monitor'];

export function getEffectiveRole(groups: string[] | undefined | null): Role | null {
  if (!groups || groups.length === 0) return null;
  for (const role of ROLE_PRECEDENCE) {
    if (groups.includes(role)) return role;
  }
  return null;
}
